import React, { useState, useEffect, useCallback } from 'react';
import { API } from '../App';
import { useT } from '../i18n';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Download, RefreshCw, Search, Users, ExternalLink } from 'lucide-react';
import XpnsrPageHeader from './XpnsrPageHeader';
import SectionIntro from './SectionIntro';
import { XpnsrTableSkeleton } from './XpnsrSkeleton';
import XpnsrEmpty from './XpnsrEmpty';

const STATUS_OPTIONS = ['accepted', 'rejected', 'error', 'lead', 'ftd', 'chargeback'];

function statusVariant(status) {
  if (status === 'accepted' || status === 'ftd') return 'success';
  if (status === 'lead') return 'secondary';
  if (status === 'rejected') return 'warning';
  if (status === 'error' || status === 'chargeback') return 'destructive';
  return 'outline';
}

export default function Leads() {
  const { t } = useT();
  const [leads, setLeads] = useState([]);
  const [offers, setOffers] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_status: {} });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [filters, setFilters] = useState({
    offer_id: '',
    status: '',
    q: '',
    date_from: '',
    date_to: '',
  });

  const buildParams = useCallback(() => {
    const params = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (v && String(v).trim()) params[k] = String(v).trim();
    });
    return params;
  }, [filters]);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const [leadsRes, statsRes] = await Promise.all([
        axios.get(`${API}/leads`, { params: buildParams() }),
        axios.get(`${API}/leads/stats`),
      ]);
      setLeads(Array.isArray(leadsRes.data) ? leadsRes.data : []);
      setStats(statsRes.data || { total: 0, by_status: {} });
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('Failed to load leads'));
    } finally {
      setLoading(false);
    }
  }, [buildParams, t]);

  const fetchOffers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/offers`);
      setOffers(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      // non-fatal: the offer filter just stays empty
    }
  }, []);

  useEffect(() => {
    fetchOffers();
  }, [fetchOffers]);

  useEffect(() => {
    fetchLeads();
  }, []);

  const applyFilters = () => fetchLeads();

  const resetFilters = () => {
    setFilters({ offer_id: '', status: '', q: '', date_from: '', date_to: '' });
    setTimeout(fetchLeads, 0);
  };

  const exportCsv = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const r = await axios.get(`${API}/leads/export-csv`, {
        params: buildParams(),
        responseType: 'blob',
      });
      const cd = r.headers?.['content-disposition'] || '';
      const match = /filename=([^;]+)/i.exec(cd);
      const fallback = `leads-${new Date().toISOString().slice(0, 10)}.csv`;
      const filename = (match && match[1].trim().replace(/^"|"$/g, '')) || fallback;
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(t('Leads exported'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('Lead export failed'));
    } finally {
      setExporting(false);
    }
  };

  const fmtDate = (value) => {
    if (!value) return '-';
    try { return new Date(value).toLocaleString(); } catch { return String(value); }
  };

  const crmSummary = (lead) => {
    if (lead.redirect_url) return t('redirect');
    if (lead.error) return lead.error;
    if (lead.http_status) return `HTTP ${lead.http_status}`;
    return '-';
  };

  const statusCards = [
    { key: 'total', label: t('Total leads'), value: stats.total || 0 },
    { key: 'accepted', label: t('Accepted'), value: stats.by_status?.['accepted'] || 0 },
    { key: 'rejected', label: t('Rejected'), value: stats.by_status?.['rejected'] || 0 },
    { key: 'error', label: t('Errors'), value: stats.by_status?.['error'] || 0 },
  ];

  return (
    <div className="p-8 space-y-6">
      <XpnsrPageHeader
        kicker={t('Leads')}
        title={t('Lead storage')}
        subtitle={t('Every submitted lead is stored here: filter, export (CSV) and resell')}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={fetchLeads} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              {t('Refresh')}
            </Button>
            <Button onClick={exportCsv} disabled={exporting || leads.length === 0}>
              <Download className={`w-4 h-4 mr-1 ${exporting ? 'animate-pulse' : ''}`} />
              {t('Export CSV')}
            </Button>
          </div>
        }
      />

      <SectionIntro className="mb-4">
        {t('Every lead captured by your form offers lands here — accepted, rejected and test. Filter, export to CSV and resell. Statuses are updated by postbacks and pull sync.')}
      </SectionIntro>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        {statusCards.map((c) => (
          <Card key={c.key}>
            <CardContent className="p-4">
              <div className="text-xs text-muted-foreground uppercase tracking-wide">{c.label}</div>
              <div className="text-2xl font-semibold mt-1">{c.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="mb-4">
        <CardContent className="p-4 grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
          <div>
            <Label className="text-xs">{t('Offer')}</Label>
            <Select
              value={filters.offer_id || 'all'}
              onValueChange={(v) => setFilters((f) => ({ ...f, offer_id: v === 'all' ? '' : v }))}
            >
              <SelectTrigger><SelectValue placeholder={t('All offers')} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('All offers')}</SelectItem>
                {offers.map((o) => (
                  <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">{t('Status')}</Label>
            <Select
              value={filters.status || 'all'}
              onValueChange={(v) => setFilters((f) => ({ ...f, status: v === 'all' ? '' : v }))}
            >
              <SelectTrigger><SelectValue placeholder={t('Any status')} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('Any status')}</SelectItem>
                {STATUS_OPTIONS.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">{t('From')}</Label>
            <Input type="date" value={filters.date_from}
              onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))} />
          </div>
          <div>
            <Label className="text-xs">{t('To')}</Label>
            <Input type="date" value={filters.date_to}
              onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))} />
          </div>
          <div>
            <Label className="text-xs">{t('Search')}</Label>
            <Input placeholder={t('email / phone / name')} value={filters.q}
              onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
              onKeyDown={(e) => { if (e.key === 'Enter') applyFilters(); }} />
          </div>
          <div className="md:col-span-5 flex gap-2">
            <Button size="sm" onClick={applyFilters}>
              <Search className="w-4 h-4 mr-1" />{t('Apply filters')}
            </Button>
            <Button size="sm" variant="ghost" onClick={resetFilters}>{t('Reset')}</Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4"><XpnsrTableSkeleton rows={8} /></div>
          ) : leads.length === 0 ? (
            <div className="p-8">
              <XpnsrEmpty
                icon={Users}
                title={t('No leads yet')}
                subtitle={t('Submitted leads from your offer forms will appear here.')}
              />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('Date')}</TableHead>
                    <TableHead>{t('Name')}</TableHead>
                    <TableHead>{t('Email')}</TableHead>
                    <TableHead>{t('Phone')}</TableHead>
                    <TableHead>{t('Geo')}</TableHead>
                    <TableHead>{t('Status')}</TableHead>
                    <TableHead>{t('CRM')}</TableHead>
                    <TableHead>{t('Offer')}</TableHead>
                    <TableHead>Click ID</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leads.map((l) => (
                    <TableRow key={l.id}>
                      <TableCell className="whitespace-nowrap text-xs">{fmtDate(l.created_at)}</TableCell>
                      <TableCell className="whitespace-nowrap">{[l.firstname, l.lastname].filter(Boolean).join(' ') || '-'}</TableCell>
                      <TableCell className="whitespace-nowrap">{l.email || '-'}</TableCell>
                      <TableCell className="whitespace-nowrap">{l.phone || '-'}</TableCell>
                      <TableCell className="whitespace-nowrap">{l.country_code || l.country || '-'}</TableCell>
                      <TableCell><Badge variant={statusVariant(l.status)}>{l.status}</Badge></TableCell>
                      <TableCell className="whitespace-nowrap text-xs">
                        {l.redirect_url ? (
                          <a href={l.redirect_url} target="_blank" rel="noreferrer"
                             className="inline-flex items-center gap-1 text-primary hover:underline">
                            {t('redirect')} <ExternalLink className="w-3 h-3" />
                          </a>
                        ) : crmSummary(l)}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">{l.offer_name || '-'}</TableCell>
                      <TableCell className="whitespace-nowrap text-xs font-mono">{l.click_id || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
